import { http } from '../http';

export async function criarVistoria(params: {
  carroId?: string;
  veiculoId?: string;
  accessories?: any;
}) {
  const response = await http.post('/vistorias/create', params);
  return response.data as { id: string; veiculo_id: string };
}

export async function uploadVistoriaPhotos(params: {
  vistoriaId: string;
  files: File[];
  metas: any[];
  onProgress?: (progress: { loaded: number; total?: number; percent?: number }) => void;
}) {
  const form = new FormData();
  params.files.forEach((f) => form.append('files', f));
  form.append('metas', JSON.stringify(params.metas || []));

  const response = await http.post(`/vistorias/${params.vistoriaId}/photos`, form, {
    // não setar Content-Type manualmente; axios adiciona boundary
    onUploadProgress: (evt: any) => {
      if (!params.onProgress) return;
      const loaded = typeof evt?.loaded === 'number' ? evt.loaded : 0;
      const total = typeof evt?.total === 'number' ? evt.total : undefined;
      const percent = total ? Math.round((loaded * 100) / total) : undefined;
      params.onProgress({ loaded, total, percent });
    },
  });

  return response.data as { fotos: any; uploaded: any[] };
}

/**
 * Grava o checklist e/ou o link do PDF de uma vistoria.
 *
 * 🔴 **`accessories` é OPCIONAL, e isso corrige um defeito que apagava dado**
 * (at-64). O backend **substitui** o campo inteiro
 * (`vistoria.service.ts`: `data: { accessories, ... }`), e o laudo enviava o
 * checklist **do Redux** ao finalizar. Ao retomar uma vistoria pela `/carteira`,
 * `hidratarFluxoVistoria` reseta aquele slice — então "Finalizar" gravava um
 * checklist vazio por cima das 23 respostas que já estavam no banco, e a trava
 * `podeFinalizar` não pegava (ela lê o banco, que estava completo).
 *
 * Hoje a finalização manda **só** `pdf_image_url`, e omitir a chave preserva o que
 * está gravado. Quem grava o checklist é a tela de checklist, que é quem tem o
 * dado.
 *
 * ⚠️ A DTO do backend já declarava `accessories` como opcional e o Prisma ignora
 * `undefined` em `data` — não houve mudança de backend.
 */
export async function atualizarVistoriaAccessories(params: {
  vistoriaId: string;
  accessories?: any;
  pdfImageUrl?: string;
}) {
  const response = await http.patch(
    `/vistorias/${params.vistoriaId}/accessories`,
    {
      ...(params.accessories !== undefined ? { accessories: params.accessories } : {}),
      ...(params.pdfImageUrl ? { pdf_image_url: params.pdfImageUrl } : {}),
    }
  );
  return response.data;
}

export async function uploadVistoriaSignature(params: {
  vistoriaId: string;
  signatureBase64: string;
}) {
  const response = await http.post(
    `/vistorias/${params.vistoriaId}/signature`,
    { signatureBase64: params.signatureBase64 }
  );
  return response.data;
}

export async function gerarLaudoPdfVistoria(params: {
  vistoriaId: string;
}) {
  const response = await http.post(`/vistorias/${params.vistoriaId}/laudo-pdf`, {});

  return response.data as { pdfUrl: string };
}

export type UltimaVistoriaPorVeiculoResponse = {
  vistoria: {
    id: string;
    veiculoId: string;
    vehicleId: string;
    createdAt: string;
    pdf_image_url?: string | null;
    associado: null | {
      id: string;
      nomeCompleto: string;
      cpfCnpj: string;
      email: string;
    };
    veiculo: {
      id: string;
      placa: string | null;
      modelo: string | null;
      anoModelo: number | null;
      anoFabricacao: number | null;
    };
    checklist: {
      bom: string[];
      ruim: string[];
      naoExistente: string[];
      observacao: string;
    };
    /** `damaged` = foto com avaria marcada pelo consultor (a marcação está queimada na imagem). */
    imagens: Array<{ url: string; legenda?: string; damaged?: boolean }>;
    assinaturaUrl?: string | null;
  };
};

export async function getUltimaVistoriaPorVeiculo(params: { vehicleId: string }) {
  // Path corrigido na at-24: o backend registra `carro/:carroId/ultima` — este cliente
  // chamava `veiculo/:id/ultima`, que NUNCA existiu. Resultado: 404 em toda chamada,
  // então "Visualizar Vistoria" sempre caía no aviso "nenhuma vistoria encontrada"
  // (mesmo com vistoria feita) e as duas telas de laudo não carregavam nada.
  // Era a causa raiz do item 35 do backlog.
  const response = await http.get(`/vistorias/carro/${params.vehicleId}/ultima`);

  return response.data as UltimaVistoriaPorVeiculoResponse;
}

export type PainelVistoriaItem = {
  carroId: string;
  placa: string | null;
  /** at-46: motocicleta elétrica — a "placa" é a identificação gerada pelo SAAC. */
  bikeEletrica?: boolean;
  modelo: string | null;
  anoModelo: number | null;
  anoFabricacao: number | null;
  tipoVeiculo: string;
  associado: {
    id: string | null;
    nomeCompleto: string | null;
    cpfCnpj: string | null;
    codigoAssociado: string | null;
    podeVistoriar: boolean;
  };
  vistoria: null | {
    id: string;
    data: string;
    /** Primeiro passo pendente; null = concluída. */
    proximoPasso: 'CHECKLIST' | 'ASSINATURA' | 'LAUDO' | null;
    concluida: boolean;
    podeDescartar: boolean;
    laudoEnviadoSga: boolean;
    fotosEnviadasSga: boolean;
    temAssinatura: boolean;
    temLaudoPdf: boolean;
    quantidadeFotos: number;
  };
};

export type PainelVistoriaResponse = {
  totalItems: number;
  page: number;
  limit: number;
  totais: {
    veiculos: number;
    semVistoria: number;
    laudosPendentesSga: number;
    associadosPendentesSga: number;
  };
  items: PainelVistoriaItem[];
};

/** Carteira do consultor logado, uma linha por veículo, com o estado da vistoria. */
export async function fetchPainelVistorias(params: {
  page?: number;
  limit?: number;
  busca?: string;
}) {
  const busca = params.busca?.trim();

  const response = await http.get('/vistorias/painel', {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      ...(busca ? { busca } : {}),
    },
  });

  return response.data as PainelVistoriaResponse;
}

/** Descarta uma vistoria em andamento (soft delete no backend). */
export async function descartarVistoria(params: { vistoriaId: string }) {
  const response = await http.patch(`/vistorias/${params.vistoriaId}/descartar`, {});

  return response.data as { descartada: boolean };
}

export type VistoriaPorIdResponse = {
  id: string;
  accessories?: { items?: Array<{ key: string; label?: string; status?: string | null }>; observacao?: string };
  /** Fotos já enviadas: cada uma com o id usado para removê-la e a legenda do slot. */
  fotos?: Array<{
    id: string;
    url: string;
    caption?: string | null;
    damaged?: boolean;
  }>;
  signature_image_url?: string | null;
  pdf_image_url?: string | null;
  /** Estado do envio ao SGA (at-31) — alimenta o encerramento na tela do laudo. */
  envioSga?: {
    laudoEnviado: boolean;
    fotosEnviadas: boolean;
    /** As fotos exigem `codigo_veiculo`; sem ele o envio delas nem é tentado. */
    veiculoNoSga: boolean;
  };
};

/**
 * Busca a vistoria pelo id. Usada para REIDRATAR o checklist ao retomar um
 * rascunho: a tela de checklist lê o Redux, que é limpo no logout — sem isto, o
 * consultor perderia as respostas já salvas no banco.
 */
export async function buscarVistoriaPorId(params: { vistoriaId: string }) {
  const response = await http.get(`/vistorias/${params.vistoriaId}`);

  return response.data as VistoriaPorIdResponse;
}

/** Remove uma foto de vistoria em andamento (permite corrigir um rascunho). */
export async function removerFotoVistoria(params: { vistoriaId: string; fotoId: string }) {
  const response = await http.delete(
    `/vistorias/${params.vistoriaId}/photos/${params.fotoId}`
  );

  return response.data as { removida: boolean; fotos: any };
}
